import React, {useCallback, useMemo, useState} from 'react';
import SMCButton from '../../../../../../components/Simple/SMCButton';
import {useTranslation} from 'react-i18next';
import {useAccessPolicyUtils} from './util';
import {useBoolean} from '../../../../../../utils/hooks';
import AccessPolicyLegacyIncomingEditorModal
    from '../../../../../PopupPages/AccessPolicy/AccessPolicyLegacyIncomingEditorModal';
import AccessPolicyLegacyOutgoingEditorModal
    from '../../../../../PopupPages/AccessPolicy/AccessPolicyLegacyOutgoingEditorModal';
import AccessPolicyNextGenIncomingEditorModal
    from '../../../../../PopupPages/AccessPolicy/AccessPolicyNextGenIncomingEditorModal';
import AccessPolicyNextGenOutgoingEditorModal
    from '../../../../../PopupPages/AccessPolicy/AccessPolicyNextGenOutgoingEditorModal';
import {CpRuleSchema} from '../../../../../../api/DBSchema/CPRule';
import newIcon from '../../../../../../assets/images/new.svg';
import addRuleAtBottom from '../../../../../../assets/images/add_rule_at_bottom.svg';
import addRuleAtTop from '../../../../../../assets/images/add_rule_at_top.svg';
import RowLayout from '../../../../../../components/Layouts/RowLayout';
import {DataGrid, Expander, useTreeStore} from "@chkp/mwc-data-grid";
import { makeStyles } from '@mui/styles';
import ColumnLayout from '../../../../../../components/Layouts/ColumnLayout';
import disabledIcon from '../../../../../../assets/images/disabled.svg';

const DELETE_BUTTON_TOOLTIP_MSG = "Delete rule";
const EDIT_BUTTON_TOOLTIP_MSG = "Edit rule";
const ADD_ABOVE_BUTTON_TOOLTIP_MSG = "Add rule above";
const ADD_BELOW_BUTTON_TOOLTIP_MSG = "Add rule below";

const onSuccessStep = {
    new: "new",
    edit: "edit",
    addAbove: "addAbove",
    addBelow: "addBelow"
}

const PRE_RULE_TYPE = "pre";
const POST_RULE_TYPE = "post";
const ABOVE_RULE = "above";
const BELOW_RULE = "below";

const dataGridRowHeight = 50;
const dataGridSectionRowHeight = 26;
const dataGridNewItemRowHeight = 55;

/**
 * @param {Object} props
 * @param {Object} [props.preRules] - array of preRules
 * @param {Object} [props.postRules] - array of postRules
 * @param {?function} setPreRules - callback for setting pre rules
 * @param {?function} setPostRules - callback for setting post rules*
 * @param {boolean} [props.disabled] - is component disabled
 * @param {string} props.baseObjectName - plan / gateway name
 * @param {string} props.objectType
 * @param {('incoming'|'outgoing')} props.incomingOrOutgoing
 * @param {('legacy'|'nextGen')} props.legacyOrNextGen
 * @param {Object} [props.appiData] - application data
 * @param {Object} [props.uoTableData]
 * @param {Object} [props.zonesData]
 */
function AccessPolicyTableNextGen({
                                      preRules,
                                      postRules,
                                      disabled,
                                      setPreRules,
                                      setPostRules,
                                      baseObjectName,
                                      objectType,
                                      incomingOrOutgoing,
                                      legacyOrNextGen,
                                      appiData,
                                      uoTableData,
                                      zonesData,
                                      setUoTableDataValue,
                                      setAppiDataValue,
                                  }) {
    const {t} = useTranslation();
    const {
        getServiceOrApplicationString,
        getNetworkObjectString,
        getActionIcon,
        getActionString,
        getLogIcon,
        getLogString
    } = useAccessPolicyUtils(appiData, zonesData, uoTableData);
    const [isPolicyEditorOpen, openPolicyEditor, closePolicyEditor] = useBoolean(false);
    const [selectedObjects, setSelectedObjects] = useState([]);

    const [onSuccessStepState, setOnSuccessStepState] = useState();

    // Editor objects
    const [editorObject, setEditorObject] = useState(null);
    const [rulePreOrPost, setRulePreOrPost] = useState(null);

    const classes = useStyles();

    const applicationOrserviceHeader = (legacyOrNextGen === 'nextGen' && incomingOrOutgoing === 'outgoing') ?
        "Application / Service" : "Service";

    //If no rules are found, return a isNewItemRow rule for row renderer
    const _preRules = useMemo(() => {
        if (!preRules?.length) {
            return [{isNewItemRow: true, isPreOrPost: PRE_RULE_TYPE, uid: "newItemRowPreRules"}];
        } else {
            // Add index to rules, and is pre or post rule
            return preRules.map((rule, i) => ({...rule, index: i, isPreOrPost: PRE_RULE_TYPE}))
        }
    }, [preRules]);

    //If no rules are found, return a newItem rule for row renderer
    const _postRules = useMemo(() => {
        if (!postRules?.length) {
            return [{isNewItemRow: true, isPreOrPost: POST_RULE_TYPE, uid: "newItemRowPostRules"}];
        } else {
            // Add index to rules, and is pre or post rule            
            return postRules.map((rule, i) => ({...rule, index: i, isPreOrPost: POST_RULE_TYPE}))
        }
    }, [postRules]);

    const getKeyFromRule = useCallback((rule) => {
        const key = (rule.index + ':' + rule.isPreOrPost);
        return key;
    }, []);

    const getRuleFromKey = useCallback((key) => {
        if (key) {
            const [rowIndex, preOrPost] = (key.split(":"));
            if (rowIndex && preOrPost) {
                if (preOrPost === PRE_RULE_TYPE) {
                    return _preRules[rowIndex]
                }
                if (preOrPost === POST_RULE_TYPE) {
                    return _postRules[rowIndex];
                } else {
                    return null;
                }
            }
        }
        return null;
    }, [_preRules, _postRules]);

    const deleteRule = useCallback(() => {
        if (selectedObjects && selectedObjects.length) {

            if (window.confirm(t('RESULTS_OBJECTS_DELETE_QUES_SHORT'))) {
                const rule = getRuleFromKey(selectedObjects[0]);
                if (!rule)
                    return;

                const ruleType = rule.isPreOrPost
                if (!ruleType) {
                    return;
                }
                if (ruleType === PRE_RULE_TYPE) {
                    setPreRules(_preRules.filter((elem, i) => {
                        return selectedObjects.indexOf(i + ':' + PRE_RULE_TYPE) === -1;
                    }));
                } else {
                    setPostRules(_postRules.filter((elem, i) => {
                        return selectedObjects.indexOf(i + ':' + POST_RULE_TYPE) === -1;
                    }));
                }
                setSelectedObjects([]);
            }

        }
    }, [getRuleFromKey, _postRules, _preRules, selectedObjects, setPostRules, setPreRules, t]);


    const editRule = useCallback(() => {
        if (selectedObjects && selectedObjects.length) {
            const rule = getRuleFromKey(selectedObjects[0]);
            if (!rule) {
                return;
            }
            const ruleType = rule.isPreOrPost
            if (!ruleType) {
                return;
            }
            if (ruleType === PRE_RULE_TYPE) {
                //Keep state of is editor rule pre or post 
                setRulePreOrPost(PRE_RULE_TYPE);
            } else if (ruleType === POST_RULE_TYPE) {
                //Keep state of is editor rule pre or post                 
                setRulePreOrPost(POST_RULE_TYPE);
            } else {
                return;
            }
            setEditorObject(rule);
            //Keep state to remember that we came from edit flow
            setOnSuccessStepState(onSuccessStep.edit);
            openPolicyEditor();
        }
    }, [openPolicyEditor, selectedObjects, getRuleFromKey]);

    const addNewRule = useCallback((preOrPost) => {
        //Keep state to remember that we came from new flow
        setOnSuccessStepState(onSuccessStep.new);
        setRulePreOrPost(preOrPost);
        openPolicyEditor()
    }, [openPolicyEditor]);

    const addRuleAbove = useCallback(() => {
        //Keep state to remember that we came from add above flow
        setOnSuccessStepState(onSuccessStep.addAbove);
        openPolicyEditor();
    }, [openPolicyEditor]);

    const addRuleBelow = useCallback(() => {
        //Keep state to remember that we came from add below flow
        setOnSuccessStepState(onSuccessStep.addBelow);
        openPolicyEditor();
    }, [openPolicyEditor]);


    const updateNewRuleFields = useCallback((rulesBeforeUpdate, newRule) =>{
        if (uoTableData.length === 0) {
            if (newRule.source.objCodeName !== '' && newRule.source.objCodeName === rulesBeforeUpdate.source.objCodeName) {
                newRule.source.updatableObjectIcon = rulesBeforeUpdate.source.updatableObjectIcon;
                newRule.source.updatableObjectName = rulesBeforeUpdate.source.updatableObjectName;
            }
            if (newRule.destination.objCodeName !== '' && newRule.destination.objCodeName === rulesBeforeUpdate.destination.objCodeName) {
                newRule.destination.updatableObjectIcon = rulesBeforeUpdate.destination.updatableObjectIcon;
                newRule.destination.updatableObjectName = rulesBeforeUpdate.destination.updatableObjectName;
            }
        }
        if (appiData.length === 0) {
            if (newRule.application.predefinedAppId !== '' && newRule.application.predefinedAppId === rulesBeforeUpdate.application.predefinedAppId) {
                newRule.application.applicationName = rulesBeforeUpdate.application.applicationName;
            }
        }
    },[appiData,uoTableData]);

    const editPolicyCallback = useCallback((newRule) => {
        let rulesCopy = "";
        if (rulePreOrPost === PRE_RULE_TYPE) {
            updateNewRuleFields(_preRules[editorObject.index], newRule);
            rulesCopy = [..._preRules];
            rulesCopy[editorObject.index] = newRule;
            setPreRules(rulesCopy);
        } else {
            updateNewRuleFields(_postRules[editorObject.index], newRule);
            rulesCopy = [..._postRules];
            rulesCopy[editorObject.index] = newRule;
            setPostRules(rulesCopy);
        }
        setEditorObject(null);
        setRulePreOrPost(null);
        setSelectedObjects([]);
        closePolicyEditor();
    }, [editorObject, closePolicyEditor, _preRules, _postRules, setPostRules, setPreRules, rulePreOrPost,updateNewRuleFields]);

    const newPolicyCallback = useCallback((newRule) => {
        let rules = "";
        if (rulePreOrPost === PRE_RULE_TYPE) {
            rules = preRules;
            const rulesCopy = rules ? [...rules] : [];
            rulesCopy.push(newRule);
            setPreRules(rulesCopy);
        } else {
            rules = postRules;
            const rulesCopy = rules ? [...rules] : [];
            rulesCopy.push(newRule);
            setPostRules(rulesCopy);
        }
        setRulePreOrPost(null);
        setSelectedObjects([]);
        closePolicyEditor();
    }, [postRules, preRules, closePolicyEditor, rulePreOrPost, setPostRules, setPreRules]);

    const addRuleAdjacentToSelectedCallback = useCallback((newRule, aboveOrBelowRule) => {
        const selectedIndex = parseInt(selectedObjects[0]);
        if (!isNaN(selectedIndex) && selectedIndex !== null) {
            const selectedRule = getRuleFromKey(selectedObjects[0]);
            if (!selectedRule) {
                return;
            }
            const ruleType = selectedRule.isPreOrPost;
            if (!ruleType) {
                closePolicyEditor();
                return;
            }
            const index = (aboveOrBelowRule === ABOVE_RULE) ? selectedIndex : selectedIndex + 1
            if (ruleType === PRE_RULE_TYPE) {
                const rulesCopy = preRules ? [...preRules] : [];
                rulesCopy.splice(index, 0, newRule);
                setPreRules(rulesCopy);
                setSelectedObjects([]);
            } else if (ruleType === POST_RULE_TYPE) {
                const rulesCopy = postRules ? [...postRules] : [];
                rulesCopy.splice(index, 0, newRule);
                setPostRules(rulesCopy);
                setSelectedObjects([]);
            }
        }
        closePolicyEditor();
    }, [closePolicyEditor, getRuleFromKey, postRules, preRules, selectedObjects, setPostRules, setPreRules]);

    const addRuleAtBottomOfSelectedCallback = useCallback((newRule) => {
        addRuleAdjacentToSelectedCallback(newRule, BELOW_RULE);
    }, [addRuleAdjacentToSelectedCallback]);

    const addRuleAtTopOfSelectedCallback = useCallback((newRule) => {
        addRuleAdjacentToSelectedCallback(newRule, ABOVE_RULE);
    }, [addRuleAdjacentToSelectedCallback]);


    const onClosePolicyEditor = useCallback(() => {
        setEditorObject(null);
        setRulePreOrPost(null);
        closePolicyEditor();
    }, [closePolicyEditor]);

    /* The editor opens for several different operations, 
    remember which operation we came from and call callback accordingly 
    */
    const editorOnSuccess = useCallback((rule) => {
        switch (onSuccessStepState) {
            case onSuccessStep.new:
                newPolicyCallback(rule);
                break;
            case onSuccessStep.edit:
                editPolicyCallback(rule);
                break;
            case onSuccessStep.addBelow:
                addRuleAtBottomOfSelectedCallback(rule);
                break;
            case onSuccessStep.addAbove:
                addRuleAtTopOfSelectedCallback(rule);
                break;
            default:
                break;
        }

    }, [addRuleAtBottomOfSelectedCallback, addRuleAtTopOfSelectedCallback, editPolicyCallback, onSuccessStepState, newPolicyCallback]);

    const EditorModal = useMemo(() => {
        if (legacyOrNextGen === 'legacy' && incomingOrOutgoing === 'incoming') {
            return AccessPolicyLegacyIncomingEditorModal;
        } else if (legacyOrNextGen === 'legacy' && incomingOrOutgoing === 'outgoing') {
            return AccessPolicyLegacyOutgoingEditorModal;
        } else if (legacyOrNextGen === 'nextGen' && incomingOrOutgoing === 'incoming') {
            return AccessPolicyNextGenIncomingEditorModal;
        } else {
            return AccessPolicyNextGenOutgoingEditorModal;
        }
    }, [legacyOrNextGen, incomingOrOutgoing]);

    const indexColumnRenderer = (obj) => {
        return <RowLayout center style={{position: 'relative', top: '30%'}}>
            <div style={{justifyContent: "center", textAlign: "center", fontSize: "14px"}}>{(obj.record.index + 1)}</div>
            {!obj.record.enabled ? (
                <img alt="" src={disabledIcon} width="70%" height="70%"></img>
            ) : null}
        </RowLayout>;
    }

    const columns = [
        {
            label: <div style={{textAlign: "center"}}>#</div>, width: 60, renderer: (obj) => {
                return indexColumnRenderer(obj)
            }
        },
        {
            label: "Source", key: CpRuleSchema.source, width: 310, renderer: (obj) => {
                return <span className={classes.gridCellAlignment}>{getNetworkObjectString(obj.value)}</span>
            }
        },
        {
            label: "Destination", key: CpRuleSchema.destination, width: 310, renderer: (obj) => {
                return <span className={classes.gridCellAlignment}>{getNetworkObjectString(obj.value)}</span>
            }
        },
        {
            label: applicationOrserviceHeader, width: 150, renderer: (obj) => {
                return (<span className={classes.gridCellAlignment}>{getServiceOrApplicationString(obj.record)}</span>)
            }
        },
        {
            label: "Action", key: CpRuleSchema.action, width: 140, renderer: (obj) => {
                return (<RowLayout className={classes.gridCellAlignment}><img align="absmiddle" alt=""
                                                                              src={getActionIcon(obj.record.action)}
                                                                              style={{paddingRight: "6px"}}/>{getActionString(obj.record.action)}
                </RowLayout>)
            }
        },
        {
            label: "Log", key: CpRuleSchema.log, width: 120, renderer: (obj) => {
                return (<RowLayout className={classes.gridCellAlignment}><img align="absmiddle" alt=""
                                                                              src={getLogIcon(obj.record.log)}
                                                                              style={{paddingRight: "6px"}}/>{getLogString(obj.record.log)}
                </RowLayout>)
            }
        },
        {
            label: "Description", key: CpRuleSchema.description, width: 190, renderer: (obj) => {
                return (<span className={classes.gridCellAlignment}>{obj.record.description}</span>)
            }
        }
    ]

    const root = {
        uid: 'root',
        children: [{
            uid: 'sectionPreRules',
            sectionTitle: t('SMP.PLAN.SEC_SOFT_BLADES.PRE_LOCAL_RULES'),
            children: _preRules
        },
            {
                uid: 'sectionPostRules',
                sectionTitle: t('SMP.PLAN.SEC_SOFT_BLADES.POST_LOCAL_RULES'),
                children: _postRules
            }
        ]
    }

    const getRowKey = (row) => {
        if (row.isPreOrPost && (row.index !== undefined)) {
            return (row.index + ':' + row.isPreOrPost)
        } else {
            return row.uid;
        }
    };

    const store = useTreeStore({root, includeRoot: false, rowKey: (obj) => getRowKey(obj)});

    /*
    Display the different types of rows
     */
    const rowRenderer = (rowContext) => {
        const {
            record,
            store: {isExpanded, setExpanded},
        } = rowContext;

        const isSection = !!record.children;
        const isNewItemRow = record && record.isNewItemRow;
        const isDisabledRow = (record && record.enabled === false) ? true : false;

        const newButtonContent = (preOrPost) => {
            return (
                <ColumnLayout center height="60px">
                    <img src={newIcon} alt="New Rule" height="20px" style={{position: 'relative', top: '10px'}}/>
                    <span>Click to add new {preOrPost} rule</span>
                </ColumnLayout>
            );
        };

        if (isSection) {
            const expanded = isExpanded(record);

            const content = (
                <>
                    <Expander
                        expanded={expanded}
                        onClick={() => setExpanded(record, !expanded)}
                    />
                    {record.sectionTitle}
                </>
            );
            // return row modifiers
            return {
                locked: true,
                content,
                style: {
                    backgroundColor: '#F2D674',
                    borderColor: '#E3DBA3',
                    paddingLeft: 16
                }
            };
        } else if (isNewItemRow) {
            const isPreOrPost = record.isPreOrPost;

            return {
                locked: true,
                className: classes.newButton,
                onClick: () => addNewRule(isPreOrPost),
                content: newButtonContent(isPreOrPost),
            };
        } else if (isDisabledRow) {
            return {
                className: classes.disabledRow
            };
        }
    };

    const dataGridGetRowHeight = (rowData) => {
        const isSection = !!rowData.children;
        const isNewItemRow = rowData.isNewItemRow;

        if (isNewItemRow) {
            return dataGridNewItemRowHeight;
        } else if (isSection) {
            return dataGridSectionRowHeight;
        } else {
            return dataGridRowHeight;
        }
    }

    const buttonIsDisabled = (disabled || (selectedObjects.length === 0) ||
        (selectedObjects[0] && (getRuleFromKey(selectedObjects[0]) == null))) ? true : false;

    return (
        <>
            {<div style={{height: 25}}>
                <SMCButton variant="ghost" disabled={buttonIsDisabled}
                           tooltip={!buttonIsDisabled ? ADD_BELOW_BUTTON_TOOLTIP_MSG: undefined} onClick={addRuleBelow}><img
                    src={addRuleAtBottom} alt=''/></SMCButton>
                <SMCButton variant="ghost" disabled={buttonIsDisabled}
                           tooltip={!buttonIsDisabled ? ADD_ABOVE_BUTTON_TOOLTIP_MSG: undefined} onClick={addRuleAbove}><img
                    src={addRuleAtTop} alt=''/></SMCButton>
                <SMCButton variant="ghost" icon="edit" disabled={buttonIsDisabled}
                           tooltip={!buttonIsDisabled ? EDIT_BUTTON_TOOLTIP_MSG: undefined} onClick={editRule}></SMCButton>
                <SMCButton variant="ghost" icon="delete" disabled={buttonIsDisabled}
                           tooltip={!buttonIsDisabled ?  DELETE_BUTTON_TOOLTIP_MSG : undefined} onClick={deleteRule}></SMCButton>
            </div>}

            <div>
                <DataGrid
                    columns={columns}
                    store={store}
                    width={1280}
                    selectedKeys={selectedObjects}
                    onSelectedKeysChange={setSelectedObjects}
                    rowKey={getKeyFromRule}
                    rowRenderer={rowRenderer}
                    fullSize={true}
                    rowHeight={dataGridGetRowHeight}
                    cellBorders={true}
                    onRowDoubleClick={editRule}
                    striped={true}
                    outline={true}
                />
            </div>

            <EditorModal
                isOpen={isPolicyEditorOpen}
                onCancel={onClosePolicyEditor}
                onSuccess={editorOnSuccess}
                defaultObject={editorObject}
                baseObjectName={baseObjectName}
                objectType={objectType}
                appiData={appiData}
                uoTableData={uoTableData}
                zonesData={zonesData}
                setUoTableDataValue={setUoTableDataValue}
                setAppiDataValue={setAppiDataValue}
            />
        </>
    );
}

const useStyles = makeStyles({
    newButton: {
        width: '100%',
        height: '100%',
        position: 'center',
        backgroundPosition: 'center',
        backgroundColor: '#DEDFE0',
        '&:hover': {
            border: '1px solid #4FB4F1'
        }
    },
    disabledRow: {
        opacity: ".5"
    },
    indexColumn: {
        backgroundColor: '#DEDFE0'
    },
    gridCellAlignment: {
        position: 'absolute',
        top: '25%',
        textAlign: "center"
    }

});

export default AccessPolicyTableNextGen;